<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, required: true },
  icon: { type: String, required: true },
  iconBgColor: { type: String, required: true },
  iconTextColor: { type: String, required: true },
  selectedSlots: { type: Array, required: true },
  disabledSlots: { type: Array, default: () => [] },
  multiSelect: { type: Boolean, default: false },
  selectedColor: { type: String, default: 'purple' },
  isGroupCalendar: { type: Boolean, default: false },
  currentSubjectLevel: { type: Object, default: null },
  showSlotDetails: { type: Boolean, default: true },
  hideHeader: { type: Boolean, default: false },
  subjects: { type: Array, default: () => [] },
  maxSlotsPerGroup: { type: Number, default: 0 },
  maxTotalSlots: { type: Number, default: 0 },
  lockedSlots: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:selectedSlots'])

// Drag selection state
const isDragging = ref(false)
const dragMode = ref(null)

const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const dayLabels = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun'
}

const timeSlots = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00']
const timeLabels = {
  '08:00': '8 AM',
  '09:00': '9 AM',
  '10:00': '10 AM',
  '11:00': '11 AM',
  '12:00': '12 PM',
  '13:00': '1 PM',
  '14:00': '2 PM',
  '15:00': '3 PM',
  '16:00': '4 PM',
  '17:00': '5 PM',
  '18:00': '6 PM',
  '19:00': '7 PM',
  '20:00': '8 PM',
  '21:00': '9 PM',
}

const buttonColors = computed(() => {
  const colors = {
    purple: {
      selected: 'border-purple-300 bg-purple-100 text-purple-700 font-semibold',
      hover: 'hover:border-purple-300 hover:bg-purple-50'
    },
    yellow: {
      selected: 'border-yellow-300 bg-yellow-100 text-yellow-700 font-semibold',
      hover: 'hover:border-yellow-300 hover:bg-yellow-50'
    }
  }
  return colors[props.selectedColor] || colors.purple
})

const createSlotKey = (day, time) => `${day}-${time}`

const isSlotDisabled = (day, time) => {
  const slotKey = createSlotKey(day, time)
  return props.disabledSlots.some(slot => {
    if (typeof slot === 'string') return slot === slotKey
    return slot.day === day && slot.time === time
  })
}

const isSlotLocked = (day, time) => {
  if (props.lockedSlots.length === 0) return false

  if (props.isGroupCalendar) {
    const slotData = getSlotData(day, time)
    if (slotData) {
      return props.lockedSlots.includes(`${day}-${time}-${slotData.subject_id}-${slotData.level_id}`)
    }
    return false
  }
  return props.lockedSlots.includes(`${day}-${time}`)
}

const isSlotSelected = (day, time) => {
  return props.selectedSlots.some(slot => {
    if (typeof slot === 'string') return slot === createSlotKey(day, time)
    return slot.day === day && slot.time === time
  })
}

const getSlotData = (day, time) => {
  return props.selectedSlots.find(slot => {
    if (typeof slot === 'object') return slot.day === day && slot.time === time
    return false
  })
}

const colorPalette = [
  { border: '#93C5FD', bg: '#DBEAFE', text: '#1E40AF' },
  { border: '#86EFAC', bg: '#D1FAE5', text: '#065F46' },
  { border: '#FCA5A5', bg: '#FEE2E2', text: '#991B1B' },
  { border: '#FCD34D', bg: '#FEF3C7', text: '#92400E' },
  { border: '#C4B5FD', bg: '#EDE9FE', text: '#5B21B6' },
  { border: '#F9A8D4', bg: '#FCE7F3', text: '#9F1239' },
  { border: '#67E8F9', bg: '#CFFAFE', text: '#155E75' },
  { border: '#FB923C', bg: '#FED7AA', text: '#9A3412' },
  { border: '#A78BFA', bg: '#DDD6FE', text: '#6B21A8' },
  { border: '#34D399', bg: '#D1FAE5', text: '#047857' },
  { border: '#F472B6', bg: '#FAE8FF', text: '#A21CAF' },
  { border: '#818CF8', bg: '#E0E7FF', text: '#4338CA' },
  { border: '#FDBA74', bg: '#FFEDD5', text: '#C2410C' },
  { border: '#2DD4BF', bg: '#CCFBF1', text: '#115E59' },
  { border: '#FB7185', bg: '#FFE4E6', text: '#BE123C' },
  { border: '#BAE6FD', bg: '#E0F2FE', text: '#0C4A6E' },
  { border: '#BBF7D0', bg: '#DCFCE7', text: '#14532D' },
  { border: '#FED7AA', bg: '#FFEDD5', text: '#7C2D12' },
  { border: '#E9D5FF', bg: '#F3E8FF', text: '#6B21A8' },
  { border: '#FBCFE8', bg: '#FCE7F3', text: '#831843' },
  { border: '#A5F3FC', bg: '#CFFAFE', text: '#164E63' },
  { border: '#FDE68A', bg: '#FEF3C7', text: '#78350F' },
  { border: '#BFDBFE', bg: '#DBEAFE', text: '#1E3A8A' },
  { border: '#6EE7B7', bg: '#D1FAE5', text: '#064E3B' },
  { border: '#FCA5A5', bg: '#FEE2E2', text: '#7F1D1D' },
  { border: '#DDD6FE', bg: '#EDE9FE', text: '#4C1D95' },
  { border: '#FCD6BB', bg: '#FFEDD5', text: '#9A3412' },
  { border: '#C7D2FE', bg: '#E0E7FF', text: '#3730A3' },
  { border: '#99F6E4', bg: '#CCFBF1', text: '#134E4A' },
  { border: '#FECACA', bg: '#FEE2E2', text: '#991B1B' },
]

const subjectLevelColorMap = computed(() => {
  const map = new Map()
  let colorIndex = 0
  props.subjects.forEach(subject => {
    if (subject.level_ids && subject.level_ids.length > 0) {
      subject.level_ids.forEach(levelId => {
        const key = `${subject.id}-${levelId}`
        map.set(key, colorIndex % colorPalette.length)
        colorIndex++
      })
    }
  })
  return map
})

const getSubjectLevelColor = (subjectId, levelId) => {
  const key = `${subjectId}-${levelId}`
  const colorIndex = subjectLevelColorMap.value.get(key) ?? 0
  return colorPalette[colorIndex]
}

const getButtonStyles = (day, time) => {
  if (!isSlotSelected(day, time)) return {}
  if (props.isGroupCalendar) {
    const slotData = getSlotData(day, time)
    if (slotData && slotData.subject_id && slotData.level_id) {
      const colors = getSubjectLevelColor(slotData.subject_id, slotData.level_id)
      return { borderColor: colors.border, backgroundColor: colors.bg, color: colors.text }
    }
  }
  return {}
}

const currentGroupSlotsUsed = computed(() => {
  if (!props.isGroupCalendar || !props.currentSubjectLevel || props.maxSlotsPerGroup <= 0) return 0
  return props.selectedSlots.filter(slot =>
    typeof slot === 'object' &&
    slot.subject_id === props.currentSubjectLevel.subject_id &&
    slot.level_id === props.currentSubjectLevel.level_id
  ).length
})

const currentGroupSlotsRemaining = computed(() => {
  if (!props.isGroupCalendar || props.maxSlotsPerGroup <= 0) return Infinity
  return props.maxSlotsPerGroup - currentGroupSlotsUsed.value
})

const isAtGroupLimit = computed(() => {
  return props.isGroupCalendar && props.maxSlotsPerGroup > 0 && currentGroupSlotsRemaining.value <= 0
})

const isAtTotalLimit = computed(() => {
  return props.maxTotalSlots > 0 && props.selectedSlots.length >= props.maxTotalSlots
})

const handleMouseDown = (day, time, event) => {
  if (isSlotDisabled(day, time)) return

  // Don't allow deselecting locked slots (active bookings)
  if (isSlotSelected(day, time) && isSlotLocked(day, time)) return

  if (props.isGroupCalendar && !props.currentSubjectLevel) return

  event.preventDefault()
  isDragging.value = true
  dragMode.value = isSlotSelected(day, time) ? 'deselect' : 'select'
  handleDragSlot(day, time)
}

const handleMouseEnter = (day, time) => {
  if (!isDragging.value) return
  handleDragSlot(day, time)
}

const handleDragSlot = (day, time) => {
  if (isSlotDisabled(day, time)) return
  if (props.isGroupCalendar && !props.currentSubjectLevel) return

  let newSlots = [...props.selectedSlots]

  if (dragMode.value === 'select') {
    const alreadySelected = newSlots.some(slot => {
      if (typeof slot === 'string') return slot === createSlotKey(day, time)
      return slot.day === day && slot.time === time
    })

    if (!alreadySelected) {
      if (props.isGroupCalendar && props.currentSubjectLevel && props.maxSlotsPerGroup > 0) {
        const currentCount = newSlots.filter(slot =>
          typeof slot === 'object' &&
          slot.subject_id === props.currentSubjectLevel.subject_id &&
          slot.level_id === props.currentSubjectLevel.level_id
        ).length
        if (currentCount >= props.maxSlotsPerGroup) return
      }

      if (props.maxTotalSlots > 0 && newSlots.length >= props.maxTotalSlots) return

      if (props.isGroupCalendar && props.currentSubjectLevel) {
        newSlots.push({
          day,
          time,
          subject_id: props.currentSubjectLevel.subject_id,
          subject_name: props.currentSubjectLevel.subject_name,
          level_id: props.currentSubjectLevel.level_id,
          level_name: props.currentSubjectLevel.level_name,
        })
      } else {
        newSlots.push(createSlotKey(day, time))
      }
    }
  } else {
    // Don't remove locked slots
    if (isSlotLocked(day, time)) return

    newSlots = newSlots.filter(slot => {
      if (typeof slot === 'string') return slot !== createSlotKey(day, time)
      return !(slot.day === day && slot.time === time)
    })
  }

  emit('update:selectedSlots', newSlots)
}

const handleMouseUp = () => {
  isDragging.value = false
  dragMode.value = null
}
</script>

<template>
  <div
    @mouseup="handleMouseUp"
    @mouseleave="handleMouseUp"
    class="select-none"
  >
    <div v-if="!hideHeader" class="flex items-center gap-3 mb-4">
      <div :class="['w-10 h-10 rounded-lg flex items-center justify-center', iconBgColor]">
        <span :class="['material-symbols-outlined', iconTextColor]">{{ icon }}</span>
      </div>
      <div>
        <h4 class="font-semibold text-gray-900">{{ title }}</h4>
        <p class="text-xs text-gray-500">{{ description }}</p>
      </div>
    </div>

    <!-- Warning if group calendar and no subject-level selected -->
    <div v-if="isGroupCalendar && !currentSubjectLevel" class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
      <div class="flex items-start gap-2">
        <span class="material-symbols-outlined text-yellow-600 text-sm mt-0.5">info</span>
        <p class="text-xs text-yellow-800">Please select a subject and level from the dropdown above to schedule slots</p>
      </div>
    </div>

    <!-- Slots remaining indicator for group calendar -->
    <div v-if="isGroupCalendar && currentSubjectLevel && maxSlotsPerGroup > 0" class="mb-4 p-3 rounded-lg" :class="isAtGroupLimit ? 'bg-orange-50 border border-orange-200' : 'bg-blue-50 border border-blue-200'">
      <div class="flex items-center gap-2">
        <span class="material-symbols-outlined text-sm" :class="isAtGroupLimit ? 'text-orange-600' : 'text-blue-600'">{{ isAtGroupLimit ? 'check_circle' : 'info' }}</span>
        <p class="text-xs" :class="isAtGroupLimit ? 'text-orange-800' : 'text-blue-800'">
          <span class="font-semibold">{{ currentGroupSlotsUsed }}/{{ maxSlotsPerGroup }}</span> session(s) selected for
          <span class="font-semibold">{{ currentSubjectLevel.subject_name }} - {{ currentSubjectLevel.level_name }}</span>
          <span v-if="isAtGroupLimit"> (limit reached)</span>
        </p>
      </div>
    </div>

    <!-- Total slots indicator for non-group calendars (1:1) -->
    <div v-if="!isGroupCalendar && maxTotalSlots > 0" class="mb-4 p-3 rounded-lg" :class="isAtTotalLimit ? 'bg-orange-50 border border-orange-200' : 'bg-blue-50 border border-blue-200'">
      <div class="flex items-center gap-2">
        <span class="material-symbols-outlined text-sm" :class="isAtTotalLimit ? 'text-orange-600' : 'text-blue-600'">{{ isAtTotalLimit ? 'check_circle' : 'info' }}</span>
        <p class="text-xs" :class="isAtTotalLimit ? 'text-orange-800' : 'text-blue-800'">
          <span class="font-semibold">{{ selectedSlots.length }}/{{ maxTotalSlots }}</span> session(s) selected
          <span v-if="isAtTotalLimit"> (limit reached)</span>
        </p>
      </div>
    </div>

    <div class="grid grid-cols-7 gap-2">
      <div v-for="day in daysOfWeek" :key="day" class="text-center">
        <p class="text-xs font-semibold text-gray-600 mb-2">{{ dayLabels[day] }}</p>
        <div class="space-y-1.5">
          <button
            v-for="time in timeSlots"
            :key="time"
            @mousedown="handleMouseDown(day, time, $event)"
            @mouseenter="handleMouseEnter(day, time)"
            :disabled="isSlotDisabled(day, time) || (isGroupCalendar && !currentSubjectLevel)"
            :class="[
              'w-full py-1.5 px-1 text-xs border-2 rounded-lg transition-colors relative',
              isSlotDisabled(day, time)
                ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                : isSlotSelected(day, time) && isSlotLocked(day, time)
                ? 'cursor-not-allowed opacity-75'
                : isSlotSelected(day, time) && !isGroupCalendar
                ? buttonColors.selected
                : !isSlotSelected(day, time)
                ? `border-gray-200 ${buttonColors.hover}`
                : '',
              (isGroupCalendar && !currentSubjectLevel) ? 'opacity-50 cursor-not-allowed' : '',
              isSlotSelected(day, time) && isGroupCalendar ? 'font-semibold' : ''
            ]"
            :style="isSlotSelected(day, time) && isGroupCalendar ? getButtonStyles(day, time) : {}"
          >
            <!-- Lock icon for booked slots -->
            <span v-if="isSlotSelected(day, time) && isSlotLocked(day, time)" class="absolute top-0.5 right-0.5 material-symbols-outlined text-[10px] text-gray-500" title="Has active booking">lock</span>
            <div class="flex flex-col">
              <span class="font-medium">{{ timeLabels[time] }}</span>
              <div
                v-if="showSlotDetails"
                :class="[
                  'text-[10px] mt-0.5 h-6 flex flex-col justify-center',
                  isSlotDisabled(day, time)
                    ? 'text-gray-400'
                    : isSlotSelected(day, time)
                    ? 'opacity-80'
                    : 'text-green-600 font-medium'
                ]"
              >
                <template v-if="isSlotDisabled(day, time)">
                  <div>Unavailable</div>
                </template>
                <template v-else-if="isSlotSelected(day, time)">
                  <template v-if="isGroupCalendar">
                    <div class="truncate uppercase leading-tight" :title="getSlotData(day, time)?.subject_name">{{ getSlotData(day, time)?.subject_name }}</div>
                    <div class="truncate !text-black leading-tight" :title="getSlotData(day, time)?.level_name">{{ getSlotData(day, time)?.level_name }}</div>
                  </template>
                  <template v-else>
                    <div class="hidden md:block">Selected</div>
                    <div class="md:hidden">✔</div>
                  </template>
                </template>
                <template v-else>
                  <div>Free</div>
                </template>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
